import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { filter, take } from 'rxjs/operators';
import { RevenueHeroModel } from 'src/app/integrations/revenuehero/revenue-hero-model';
import { MeetingBookedEvent } from 'src/app/integrations/revenuehero/types';
import { NavatticExperimentService } from 'src/app/services/navattic-experiment.service';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { AuthState } from 'src/app/store/auth/auth.state';
import { PopLoading, PushLoading } from 'src/app/store/loading/loading.actions';
import { environment } from 'src/environments/environment';
import { AuthUser } from 'src/models';

/**
 * Service that provides shared actions for Navattic tutorial features.
 * Extracted from widget components to be reused by menu items.
 */
@Injectable({
  providedIn: 'root',
})
export class NavatticActionsService {
  constructor(
    private readonly navatticExperimentService: NavatticExperimentService,
    private readonly segment: SegmentService,
    private readonly store: Store,
  ) { }

  /**
   * Starts the Navattic tutorial (same logic as StartDemoWidgetComponent).
   * Emits a custom event that app.component.ts listens to, which then calls startNavatticTutorial().
   * This allows menu items to trigger the tutorial flow without direct coupling to app.component.
   */
  startTutorial(): void {
    const event = new CustomEvent('navattic-start-tutorial');
    window.dispatchEvent(event);
  }

  /**
   * Opens the Book a Call widget (same logic as BookCallWidgetComponent).
   */
  bookCall(): void {
    this.segment.trackInAppEvents('Book Call Widget Clicked', {
      widgetType: 'book-call',
      action: 'button-click',
      location: 'menu',
    });

    this.contactSales();
  }

  /**
   * Initializes RevenueHero for booking a call.
   */
  private contactSales(): void {
    this.store.dispatch(new PushLoading());

    const user = this.store.selectSnapshot(AuthState.user);
    if (user?.email) {
      this.initializeRevenueHero(user);
      return;
    }

    this.store.select(AuthState.user).pipe(
      filter((loadedUser): loadedUser is AuthUser => !!loadedUser?.email),
      take(1),
    ).subscribe((loadedUser) => {
      this.initializeRevenueHero(loadedUser);
    });
  }

  private initializeRevenueHero(user: AuthUser): void {
    // Create a new instance each time to ensure fresh user data
    // This prevents stale data when users switch accounts without page reload
    const revenueHeroInit = new RevenueHeroModel({
      user,
      routerId: environment.trialWidgetRevenueHeroRouterId,
      scheduleId: environment.trialWidgetRevenueHeroScheduleId,
    });

    revenueHeroInit.initialize();
    this.store.dispatch(new PopLoading());

    revenueHeroInit.onMeetingBooked = (e: MeetingBookedEvent) => {
      this.trackContactSales(e);
    };
  }

  private trackContactSales(e: MeetingBookedEvent): void {
    const {
      booker_email: bookerEmail,
      booking_participants: bookingParticipants,
      booker_name: bookerName,
      reschedule_count: rescheduleCount,
      status: meetingStatus,
    } = e.data.meeting.attributes;

    this.segment.trackInAppEvents('Book a Call Confirmation Pop up', {
      bookingParticipants,
      bookerEmail,
      bookerName,
      rescheduleCount,
      meetingStatus,
      location: 'menu',
    });
  }
}
